import { useEffect, useRef } from "react";
import { useApolloClient } from "@apollo/client/react";

import { IssueSummaryFragmentDoc, type IssueRow } from "#app/lib/data";

export type MapIssueChange = { issueId: string; removed: boolean };

/**
 * What the map's pins read off a project — where it sits, its stage color and
 * workflow glyph, whether it is on the map at all — plus the fields the common
 * map filters test (type, people, teams). The map query is `no-cache`, so a
 * change here is invisible to the pins until the map asks again.
 */
export function mapIssueSignature(issue: Partial<IssueRow>): string {
  return JSON.stringify([
    issue.stageId,
    issue.workflowId,
    issue.issueTypeId,
    issue.addressId,
    issue.address?.latLng?.lat,
    issue.address?.latLng?.lng,
    issue.archived,
    issue.trashedAt,
    issue.assignees?.map((a) => a.entityId),
    issue.teams?.map((t) => t.teamId),
  ]);
}

/**
 * Calls `onChange` when a watched project's pin-relevant fields change in the
 * Apollo cache — whoever wrote them: the map card, the Project panel, a live
 * update from another session. `removed` is true for a trash or an eviction.
 * Reads the committed cache only (`optimistic: false`), so the map's re-ask
 * goes out after the server has the change, never racing its own mutation.
 * The first answer per project is the baseline and reports nothing.
 */
export function useMapIssueChanges(
  issueIds: readonly string[],
  onChange: (change: MapIssueChange) => void,
): void {
  const client = useApolloClient();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const key = [...new Set(issueIds)].sort().join(",");

  useEffect(() => {
    if (!key) return;
    const subscriptions = key.split(",").map((issueId) => {
      let last: string | null = null;
      return client.cache
        .watchFragment({
          fragment: IssueSummaryFragmentDoc,
          fragmentName: "IssueSummary",
          from: { __typename: "Issue", issueId },
          optimistic: false,
        })
        .subscribe((result) => {
          // An incomplete read is a project not loaded yet, or one evicted
          // after it was: only the second is a change.
          if (!result.complete && (last === null || last === "gone")) return;
          const data = result.complete
            ? (result.data as Partial<IssueRow>)
            : null;
          const signature = data ? mapIssueSignature(data) : "gone";
          const previous = last;
          last = signature;
          if (previous === null || previous === signature) return;
          onChangeRef.current({ issueId, removed: !data || !!data.trashedAt });
        });
    });
    return () => subscriptions.forEach((s) => s.unsubscribe());
  }, [client, key]);
}
